import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatCard, EmptyState, ListSkeleton } from "@/components/common";
import { fromNow, fmtDate, initials } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Users, FolderKanban, ListTodo, CheckCircle2, ClipboardList, Image as ImageIcon,
  ShoppingCart, StickyNote, ShieldCheck, Plus, Trash2, UserCog, CalendarClock, FileCheck,
  Building2, FolderPlus, Loader2, Upload, Check, X,
} from "lucide-react";

const ROLE_OPTIONS = [
  { value: "admin", label: "Kuruluş Yöneticisi" },
  { value: "manager", label: "Yönetici" },
  { value: "employee", label: "Çalışan" },
];

const PR_STATUS = {
  pending_management: "Yönetim onayı bekliyor",
  pending_finance: "Finans onayı bekliyor",
  revision_proposed: "Revize önerildi",
};

export default function AdminPanel() {
  const qc = useQueryClient();
  const { user, orgs, currentOrg, refresh } = useAuth();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [invite, setInvite] = useState({ name: "", email: "", role: "employee", password: "WorkHub2026!", orgIds: [] });
  const [removeTarget, setRemoveTarget] = useState(null);
  const [orgOpen, setOrgOpen] = useState(false);
  const [orgForm, setOrgForm] = useState({ name: "", currency: "TRY", primary_color: "#4F46E5", logo: "" });
  const [orgLogoUploading, setOrgLogoUploading] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [deferTarget, setDeferTarget] = useState(null);
  const [deferDate, setDeferDate] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["admin-overview"],
    queryFn: async () => (await api.get("/admin/overview")).data,
    refetchInterval: 30000,
  });

  const refetchAll = () => {
    qc.invalidateQueries({ queryKey: ["admin-overview"] });
  };

  const decide = async (kind, it, decision, opts = {}) => {
    try {
      if (kind === "purchasing") {
        if (decision === "deferred") {
          await api.post(`/purchase-requests/${it.id}/management-decision`, { decision: "deferred", scheduled_date: opts.scheduled_date, comment: opts.comment || "" });
        } else {
          const ep = it.status === "pending_finance" ? "finance-decision" : "management-decision";
          await api.post(`/purchase-requests/${it.id}/${ep}`, { decision, comment: opts.comment || "" });
        }
      } else if (kind === "designs") {
        await api.post(`/designs/${it.id}/decision`, { decision, comment: opts.comment || "" });
      } else if (kind === "leave") {
        await api.patch(`/leave-requests/${it.id}`, { status: decision, reason: opts.comment || "" });
      } else if (kind === "requests") {
        await api.post(`/requests/${it.id}/decision`, { decision, comment: opts.comment || "" });
      }
      toast.success(decision === "approved" ? "Onaylandı" : decision === "deferred" ? "İleri tarihe ertelendi" : "Reddedildi");
      refetchAll();
    } catch (e) {
      toast.error(apiError(e?.response?.data?.detail));
    }
  };
  const openReject = (kind, it) => { setRejectReason(""); setRejectTarget({ kind, it }); };
  const openDefer = (kind, it) => { setDeferDate(""); setDeferTarget({ kind, it }); };
  const confirmReject = () => { if (rejectTarget) { decide(rejectTarget.kind, rejectTarget.it, "rejected", { comment: rejectReason }); setRejectTarget(null); } };
  const confirmDefer = () => { if (deferTarget && deferDate) { decide("purchasing", deferTarget.it, "deferred", { scheduled_date: deferDate }); setDeferTarget(null); } };

  const openInvite = () => {
    setInvite({ name: "", email: "", role: "employee", password: "WorkHub2026!", orgIds: currentOrg ? [currentOrg.id] : [] });
    setInviteOpen(true);
  };

  const toggleInviteOrg = (id) => {
    setInvite((v) => ({
      ...v,
      orgIds: v.orgIds?.includes(id) ? v.orgIds.filter((x) => x !== id) : [...(v.orgIds || []), id],
    }));
  };

  const submitInvite = async () => {
    if (!invite.name.trim() || !invite.email.trim()) {
      toast.error("İsim ve e-posta gerekli");
      return;
    }
    if (!invite.orgIds || invite.orgIds.length === 0) {
      toast.error("En az bir kuruluş seçin");
      return;
    }
    try {
      const { data } = await api.post("/org/members", {
        name: invite.name, email: invite.email, role: invite.role,
        password: invite.password, org_ids: invite.orgIds,
      });
      toast.success(`Kullanıcı ${(data?.added?.length || 1)} kuruluşa eklendi`);
      setInviteOpen(false);
      refetchAll();
    } catch (e) {
      toast.error(apiError(e?.response?.data?.detail));
    }
  };

  const uploadOrgLogo = async (file) => {
    if (!file) return;
    setOrgLogoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setOrgForm((f) => ({ ...f, logo: data.storage_path }));
      toast.success("Logo yüklendi");
    } catch {
      toast.error("Logo yüklenemedi");
    } finally {
      setOrgLogoUploading(false);
    }
  };

  const submitOrg = async () => {
    if (!orgForm.name.trim()) {
      toast.error("Kuruluş adı gerekli");
      return;
    }
    try {
      await api.post("/orgs", {
        name: orgForm.name.trim(), currency: orgForm.currency,
        primary_color: orgForm.primary_color, logo: orgForm.logo,
      });
      toast.success("Kuruluş oluşturuldu");
      setOrgOpen(false);
      setOrgForm({ name: "", currency: "TRY", primary_color: "#4F46E5", logo: "" });
      await refresh();
    } catch (e) {
      toast.error(apiError(e?.response?.data?.detail));
    }
  };

  const changeRole = async (uid, role) => {
    try {
      await api.patch(`/org/members/${uid}`, { role });
      toast.success("Rol güncellendi");
      refetchAll();
    } catch (e) {
      toast.error(apiError(e?.response?.data?.detail));
    }
  };

  const doRemove = async () => {
    if (!removeTarget) return;
    try {
      await api.delete(`/org/members/${removeTarget.id}`);
      toast.success("Kullanıcı kaldırıldı");
      setRemoveTarget(null);
      refetchAll();
    } catch (e) {
      toast.error(apiError(e?.response?.data?.detail));
    }
  };

  if (isLoading) return <div className="space-y-4"><PageHeader title="Yönetim Paneli" /><ListSkeleton rows={6} /></div>;

  const stats = data?.stats || {};
  const users = data?.users || [];
  const pending = data?.pending || { purchasing: [], designs: [], leave: [], requests: [], total: 0 };

  const STAT_TILES = [
    { icon: Users, label: "Kullanıcılar", value: stats.users, tone: "indigo" },
    { icon: FolderKanban, label: "Projeler", value: stats.projects, tone: "violet" },
    { icon: ListTodo, label: "Açık Görevler", value: stats.open_tasks, tone: "amber" },
    { icon: CheckCircle2, label: "Tamamlanan", value: stats.completed_tasks, tone: "emerald" },
    { icon: ClipboardList, label: "Toplantılar", value: stats.meetings, tone: "sky" },
    { icon: ImageIcon, label: "Tasarımlar", value: stats.designs, tone: "rose" },
    { icon: ShoppingCart, label: "Satınalma", value: stats.purchase_requests, tone: "indigo" },
    { icon: StickyNote, label: "Notlar", value: stats.notes, tone: "amber" },
  ];

  return (
    <div className="space-y-6" data-testid="admin-panel">
      <PageHeader
        title="Yönetim Paneli"
        subtitle={data?.org_name ? `${data.org_name} • Sistem yönetimi ve onay merkezi` : "Sistem yönetimi ve onay merkezi"}
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setOrgOpen(true)} data-testid="admin-new-org-button">
              <FolderPlus className="h-4 w-4 mr-2" /> Yeni Kuruluş
            </Button>
            <Button onClick={openInvite} data-testid="admin-invite-button">
              <Plus className="h-4 w-4 mr-2" /> Kullanıcı Ekle
            </Button>
          </div>
        }
      />

      {/* System statistics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="admin-stats">
        {STAT_TILES.map((s) => (
          <StatCard key={s.label} icon={s.icon} label={s.label} value={s.value ?? 0} tone={s.tone}
            testId={`admin-stat-${s.label}`} />
        ))}
      </div>

      <Tabs defaultValue="approvals" className="w-full">
        <TabsList data-testid="admin-tabs">
          <TabsTrigger value="approvals" data-testid="admin-tab-approvals">
            Bekleyen Onaylar
            {pending.total > 0 && <Badge className="ml-2 bg-rose-600" data-testid="admin-pending-count">{pending.total}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users">Kullanıcı Yönetimi</TabsTrigger>
          <TabsTrigger value="orgs" data-testid="admin-tab-orgs">Kuruluşlar</TabsTrigger>
        </TabsList>

        {/* Pending approvals */}
        <TabsContent value="approvals" className="mt-4">
          {pending.total === 0 ? (
            <EmptyState icon={FileCheck} title="Bekleyen onay yok" description="Tüm onaylar tamamlandı." />
          ) : (
            <div className="space-y-6">
              <ApprovalGroup icon={ShoppingCart} title="Satınalma Onayları" to="/purchasing"
                items={pending.purchasing} testId="admin-pending-purchasing" kind="purchasing"
                onDecide={decide} onReject={openReject} onDefer={openDefer}
                render={(p) => ({ primary: p.title, meta: `${p.created_by_name || ""} • ${(p.amount || 0).toLocaleString("tr-TR")} ${p.currency || "TRY"}`, status: PR_STATUS[p.status] || p.status, date: p.created_at })} />
              <ApprovalGroup icon={ImageIcon} title="Tasarım Onayları" to="/designs"
                items={pending.designs} testId="admin-pending-designs" kind="designs"
                onDecide={decide} onReject={openReject} onDefer={openDefer}
                render={(d) => ({ primary: d.title, meta: d.created_by_name || d.uploaded_by_name || "", status: "Onay bekliyor", date: d.created_at })} />
              <ApprovalGroup icon={CalendarClock} title="İzin Talepleri" to="/requests"
                items={pending.leave} testId="admin-pending-leave" kind="leave"
                onDecide={decide} onReject={openReject} onDefer={openDefer}
                render={(l) => ({ primary: l.user_name || "İzin talebi", meta: `${fmtDate(l.start_date)} - ${fmtDate(l.end_date)}`, status: "Onay bekliyor", date: l.created_at })} />
              <ApprovalGroup icon={FileCheck} title="Diğer Talepler" to="/requests"
                items={pending.requests} testId="admin-pending-requests" kind="requests"
                onDecide={decide} onReject={openReject} onDefer={openDefer}
                render={(r) => ({ primary: r.title, meta: r.created_by_name || "", status: "Onay bekliyor", date: r.created_at })} />
            </div>
          )}
        </TabsContent>

        {/* User management */}
        <TabsContent value="users" className="mt-4">
          <Card className="divide-y" data-testid="admin-users-list">
            {users.map((u) => {
              const isSelf = u.id === user?.id;
              const isOwner = u.role === "owner";
              return (
                <div key={u.id} className="flex items-center gap-3 p-4" data-testid={`admin-user-${u.id}`}>
                  <Avatar className="h-9 w-9"><AvatarFallback className="bg-indigo-600 text-white text-xs">{initials(u.name)}</AvatarFallback></Avatar>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{u.name} {isSelf && <span className="text-xs text-muted-foreground">(siz)</span>}</p>
                    <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                  </div>
                  {isOwner ? (
                    <Badge variant="secondary" className="gap-1"><ShieldCheck className="h-3 w-3" /> Kuruluş Sahibi</Badge>
                  ) : (
                    <Select value={u.role} onValueChange={(v) => changeRole(u.id, v)}>
                      <SelectTrigger className="w-40 h-9" data-testid={`admin-role-select-${u.id}`}>
                        <UserCog className="h-3.5 w-3.5 mr-1 opacity-60" />
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ROLE_OPTIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                  {!isOwner && !isSelf && (
                    <Button variant="ghost" size="icon" className="text-rose-600" onClick={() => setRemoveTarget(u)}
                      data-testid={`admin-remove-user-${u.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              );
            })}
          </Card>
        </TabsContent>

        {/* Organizations */}
        <TabsContent value="orgs" className="mt-4">
          <Card className="divide-y" data-testid="admin-orgs-list">
            {(orgs || []).map((o) => (
              <div key={o.id} className="flex items-center gap-3 p-4" data-testid={`admin-org-${o.id}`}>
                <div className="h-9 w-9 rounded-lg grid place-items-center shrink-0 overflow-hidden"
                  style={{ backgroundColor: o.primary_color || "#4F46E5" }}>
                  {o.logo ? <img src={fileUrl(o.logo)} alt="" className="h-full w-full object-cover" />
                    : <Building2 className="h-4 w-4 text-white" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{o.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{o.role_label} • {o.currency || "TRY"}</p>
                </div>
                {o.id === currentOrg?.id && <Badge variant="secondary" className="text-[10px]">Aktif</Badge>}
              </div>
            ))}
          </Card>
          <p className="text-xs text-muted-foreground mt-3">
            Kuruluşlar arasında geçiş yapmak için üstteki kuruluş menüsünü kullanın.
          </p>
        </TabsContent>
      </Tabs>

      {/* Invite dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent data-testid="admin-invite-dialog">
          <DialogHeader>
            <DialogTitle>Kullanıcı Ekle</DialogTitle>
            <DialogDescription>Yeni bir ekip üyesi ekleyin. İlk şifre ile giriş yapıp değiştirebilirler.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Ad Soyad</Label><Input value={invite.name} onChange={(e) => setInvite({ ...invite, name: e.target.value })} data-testid="admin-invite-name" /></div>
            <div><Label>E-posta</Label><Input type="email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} data-testid="admin-invite-email" /></div>
            <div>
              <Label>Rol</Label>
              <Select value={invite.role} onValueChange={(v) => setInvite({ ...invite, role: v })}>
                <SelectTrigger data-testid="admin-invite-role"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLE_OPTIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>İlk Şifre</Label><Input value={invite.password} onChange={(e) => setInvite({ ...invite, password: e.target.value })} data-testid="admin-invite-password" /></div>
            <div>
              <Label>Kuruluşlar</Label>
              <p className="text-xs text-muted-foreground mb-2">Kullanıcının ekleneceği kuruluş(lar)ı seçin.</p>
              <div className="space-y-2 max-h-40 overflow-y-auto border rounded-lg p-2" data-testid="admin-invite-orgs">
                {(orgs || []).map((o) => (
                  <label key={o.id} className="flex items-center gap-2 text-sm cursor-pointer p-1 rounded hover:bg-muted/50">
                    <Checkbox
                      checked={invite.orgIds?.includes(o.id)}
                      onCheckedChange={() => toggleInviteOrg(o.id)}
                      data-testid={`admin-invite-org-${o.id}`}
                    />
                    <span className="truncate">{o.name}</span>
                    {o.id === currentOrg?.id && <span className="text-[10px] text-muted-foreground">(aktif)</span>}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteOpen(false)}>İptal</Button>
            <Button onClick={submitInvite} data-testid="admin-invite-submit">Ekle</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove confirm */}
      <AlertDialog open={!!removeTarget} onOpenChange={(o) => !o && setRemoveTarget(null)}>
        <AlertDialogContent data-testid="admin-remove-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Kullanıcıyı kaldır</AlertDialogTitle>
            <AlertDialogDescription>
              {removeTarget?.name} kuruluştan kaldırılacak. Bu işlem geri alınamaz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>İptal</AlertDialogCancel>
            <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={doRemove} data-testid="admin-remove-confirm">Kaldır</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* New organization dialog */}
      <Dialog open={orgOpen} onOpenChange={setOrgOpen}>
        <DialogContent data-testid="admin-new-org-dialog">
          <DialogHeader>
            <DialogTitle>Yeni Kuruluş</DialogTitle>
            <DialogDescription>Yeni bir kuruluş oluşturun. Sahibi siz olacaksınız ve üstteki menüden geçiş yapabilirsiniz.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>Kuruluş Adı</Label>
              <Input value={orgForm.name} onChange={(e) => setOrgForm({ ...orgForm, name: e.target.value })}
                onKeyDown={(e) => { if (e.key === "Enter") submitOrg(); }} data-testid="admin-new-org-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Para Birimi</Label>
                <Select value={orgForm.currency} onValueChange={(v) => setOrgForm({ ...orgForm, currency: v })}>
                  <SelectTrigger data-testid="admin-new-org-currency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="TRY">₺ TRY</SelectItem>
                    <SelectItem value="USD">$ USD</SelectItem>
                    <SelectItem value="EUR">€ EUR</SelectItem>
                    <SelectItem value="GBP">£ GBP</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Marka Rengi</Label>
                <div className="flex items-center gap-2 h-10">
                  <input type="color" value={orgForm.primary_color}
                    onChange={(e) => setOrgForm({ ...orgForm, primary_color: e.target.value })}
                    className="h-9 w-14 rounded cursor-pointer border" data-testid="admin-new-org-color" />
                  <span className="text-xs text-muted-foreground">{orgForm.primary_color}</span>
                </div>
              </div>
            </div>
            <div>
              <Label>Logo (opsiyonel)</Label>
              <div className="flex items-center gap-3 mt-1">
                <div className="h-12 w-12 rounded-lg grid place-items-center overflow-hidden shrink-0"
                  style={{ backgroundColor: orgForm.primary_color }}>
                  {orgForm.logo ? <img src={fileUrl(orgForm.logo)} alt="" className="h-full w-full object-cover" />
                    : <Building2 className="h-5 w-5 text-white" />}
                </div>
                <label className="text-sm">
                  <span className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer hover:bg-muted transition-colors" data-testid="admin-new-org-logo-btn">
                    {orgLogoUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                    {orgForm.logo ? "Değiştir" : "Logo Yükle"}
                  </span>
                  <input type="file" accept="image/*" className="hidden"
                    onChange={(e) => uploadOrgLogo(e.target.files?.[0])} data-testid="admin-new-org-logo-input" />
                </label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOrgOpen(false)}>İptal</Button>
            <Button onClick={submitOrg} data-testid="admin-new-org-submit">Oluştur</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject with reason */}
      <Dialog open={!!rejectTarget} onOpenChange={(v) => !v && setRejectTarget(null)}>
        <DialogContent data-testid="admin-reject-dialog">
          <DialogHeader>
            <DialogTitle>Reddet</DialogTitle>
            <DialogDescription>Kısa bir red gerekçesi girin. Talep sahibine bildirim olarak iletilecek.</DialogDescription>
          </DialogHeader>
          <Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} rows={3}
            placeholder="Örn. Bütçe uygun değil / eksik bilgi..." data-testid="admin-reject-reason" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectTarget(null)}>İptal</Button>
            <Button className="bg-rose-600 hover:bg-rose-700" onClick={confirmReject} data-testid="admin-reject-confirm">Reddet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Defer purchase to a later date */}
      <Dialog open={!!deferTarget} onOpenChange={(v) => !v && setDeferTarget(null)}>
        <DialogContent data-testid="admin-defer-dialog">
          <DialogHeader>
            <DialogTitle>İleri Tarihe Ertele</DialogTitle>
            <DialogDescription>Bu satınalma ileri bir tarihte yapılacak. Planlanan tarihi seçin; talep sahibi bilgilendirilir.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>Planlanan Alım Tarihi</Label>
            <Input type="date" value={deferDate} onChange={(e) => setDeferDate(e.target.value)} data-testid="admin-defer-date" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeferTarget(null)}>İptal</Button>
            <Button className="bg-orange-600 hover:bg-orange-700" disabled={!deferDate} onClick={confirmDefer} data-testid="admin-defer-confirm">Ertele</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ApprovalGroup({ icon: Icon, title, items, render, to, testId, kind, onDecide, onReject, onDefer }) {
  if (!items || items.length === 0) return null;
  return (
    <div data-testid={testId}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="h-4 w-4 text-indigo-600" />
        <h3 className="text-sm font-semibold">{title}</h3>
        <Badge variant="secondary" className="text-[10px]">{items.length}</Badge>
        <a href={to} className="ml-auto text-xs text-indigo-600 hover:underline">Tümünü aç →</a>
      </div>
      <Card className="divide-y">
        {items.map((it) => {
          const r = render(it);
          const canAct = !(kind === "purchasing" && it.status === "revision_proposed");
          const canDefer = kind === "purchasing" && it.status === "pending_management";
          return (
            <div key={it.id} className="flex items-center gap-2 p-3">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{r.primary}</p>
                <p className="text-xs text-muted-foreground truncate">{r.meta}</p>
              </div>
              <Badge variant="outline" className="text-[10px] shrink-0 hidden sm:inline-flex">{r.status}</Badge>
              {canAct ? (
                <div className="flex gap-1 shrink-0">
                  {canDefer && (
                    <Button size="sm" variant="outline" className="h-8 px-2.5 text-orange-600 border-orange-300 hover:bg-orange-50" title="İleri Tarihe Ertele" onClick={() => onDefer(kind, it)} data-testid={`defer-${kind}-${it.id}`}><CalendarClock className="h-4 w-4" /></Button>
                  )}
                  <Button size="sm" className="h-8 px-2.5 bg-emerald-600 hover:bg-emerald-700" onClick={() => onDecide(kind, it, "approved")} data-testid={`approve-${kind}-${it.id}`}><Check className="h-4 w-4" /></Button>
                  <Button size="sm" variant="outline" className="h-8 px-2.5 text-rose-600" onClick={() => onReject(kind, it)} data-testid={`reject-${kind}-${it.id}`}><X className="h-4 w-4" /></Button>
                </div>
              ) : (
                <a href={to} className="text-xs text-primary shrink-0">Aç →</a>
              )}
            </div>
          );
        })}
      </Card>
    </div>
  );
}
